import { useRef, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import { useBoxMovements, useUndoBoxMovement } from "@/features/checkin/hooks";
import type {
  LinkedBoxMovement,
  SupplierBoxSummary,
} from "@/features/checkin/types";
import { getCheckinActionError } from "@/features/checkin/itemUi";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import CheckinActionConfirmDialog from "./CheckinActionConfirmDialog";

const reasons: Record<NonNullable<LinkedBoxMovement["undo_reason"]>, string> = {
  legacy_ambiguous: "Registro antigo sem vínculo confiável",
  invalid_movement: "Movimentação inválida",
  already_reversed: "Já desfeita",
  checkin_closed: "Reabra a carga antes de desfazer",
  insufficient_balance: "Saldo insuficiente para desfazer",
};

const signed = (quantity: number) => `${quantity > 0 ? "+" : ""}${quantity}`;
const date = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/Sao_Paulo",
});

export function BoxMovementHistoryDrawer({
  open,
  checkinId,
  supplier,
  readOnly,
  closed,
  stale,
  onError,
  onClose,
}: {
  open: boolean;
  checkinId: string;
  supplier: SupplierBoxSummary;
  readOnly: boolean;
  closed: boolean;
  stale: boolean;
  onError: (message: string) => void;
  onClose: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<LinkedBoxMovement | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const history = useBoxMovements(
    checkinId,
    supplier.supplier_id,
    offset,
    open,
  );
  const undo = useUndoBoxMovement(checkinId, supplier.supplier_id);
  const close = () => {
    if (undo.isPending || selected) return;
    setOffset(0);
    setUndoError(null);
    onClose();
  };
  useDialogFocus(dialogRef, close, {
    enabled: open && !selected,
    initialFocusRef: closeRef,
  });
  if (!open) return null;

  async function confirmUndo() {
    if (!selected || !selected.undoable || readOnly || stale || undo.isPending)
      return;
    try {
      setUndoError(null);
      await undo.mutateAsync(selected.movement_id);
      setSelected(null);
    } catch (error) {
      const message = getCheckinActionError(error);
      setUndoError(message);
      onError(message);
      setSelected(null);
      void history.refetch();
    }
  }

  return (
    <div className="checkin-history-layer" onMouseDown={close}>
      <section
        ref={dialogRef}
        className="checkin-history-drawer detail-modal history-drawer box-history-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Histórico de caixas · ${supplier.supplier_name}`}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-modal-head">
          <div>
            <span>CAIXAS NESTA CARGA</span>
            <h2>{supplier.supplier_name}</h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label="Fechar histórico"
          >
            <X size={18} />
          </button>
        </header>
        <div className="detail-modal-body">
          <p className="box-history-balance">
            Saldo global atual:{" "}
            <strong>{signed(supplier.box_balance)} caixas</strong>
          </p>
          {history.isFetching ? (
            <p role="status">Carregando histórico…</p>
          ) : null}
          {history.isError ? (
            <div role="alert">
              Não foi possível carregar o histórico.{" "}
              <button type="button" onClick={() => void history.refetch()}>
                Tentar novamente
              </button>
            </div>
          ) : null}
          {!history.isFetching &&
          !history.isError &&
          !history.data?.items.length ? (
            <p>Nenhuma movimentação nesta carga.</p>
          ) : null}
          {!history.isError && !history.isFetching ? (
            <ol className="box-history-list">
              {history.data?.items.map((movement) => (
                <li key={movement.movement_id} className="box-history-entry">
                  <div className="box-history-entry-head">
                    <strong>{signed(movement.net_delta)} caixas</strong>
                    <time dateTime={movement.created_at}>
                      {date.format(new Date(movement.created_at))}
                    </time>
                  </div>
                  <p>
                    {movement.boxes_received} recebidas · {movement.boxes_sent}{" "}
                    enviadas
                  </p>
                  <small>{movement.created_by_name || "Usuário"}</small>
                  <button
                    type="button"
                    className="history-event-undo"
                    disabled={
                      !movement.undoable || readOnly || stale || undo.isPending
                    }
                    onClick={() => {
                      setUndoError(null);
                      setSelected(movement);
                    }}
                    aria-label={`Desfazer movimentação de ${date.format(new Date(movement.created_at))}`}
                  >
                    <RotateCcw size={16} /> Desfazer
                  </button>
                  {!movement.undoable || readOnly || stale ? (
                    <p className="history-event-reason">
                      {movement.reversed
                        ? "Já desfeita"
                        : readOnly && movement.undoable
                          ? closed
                            ? "Reabra a carga antes de desfazer"
                            : "Somente leitura"
                          : stale && movement.undoable
                            ? "Atualize o saldo antes de desfazer"
                            : movement.undo_reason
                              ? reasons[movement.undo_reason]
                              : "Indisponível para desfazer"}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}
          {!history.isError &&
          !history.isFetching &&
          (offset > 0 || history.data?.has_more) ? (
            <nav
              className="box-history-pages"
              aria-label="Páginas do histórico"
            >
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset((value) => Math.max(0, value - 20))}
              >
                Anterior
              </button>
              <span>Página {offset / 20 + 1}</span>
              <button
                type="button"
                disabled={!history.data?.has_more}
                onClick={() => setOffset((value) => value + 20)}
              >
                Próxima
              </button>
            </nav>
          ) : null}
        </div>
      </section>
      <CheckinActionConfirmDialog
        open={Boolean(selected)}
        title="Desfazer movimentação de caixas"
        description={
          selected
            ? `Desfazer ${selected.boxes_received} recebidas e ${selected.boxes_sent} enviadas? Efeito no saldo: ${signed(-selected.net_delta)} caixas. Saldo global: ${signed(supplier.box_balance)} → ${signed(supplier.box_balance - selected.net_delta)} caixas.`
            : ""
        }
        confirmLabel="Desfazer movimentação"
        pending={undo.isPending}
        confirmDisabled={Boolean(
          readOnly ||
          stale ||
          history.isFetching ||
          history.isError ||
          !history.data?.items.some(
            (movement) =>
              movement.movement_id === selected?.movement_id &&
              movement.undoable,
          ),
        )}
        error={undoError}
        onCancel={() => {
          if (!undo.isPending) {
            setSelected(null);
            setUndoError(null);
          }
        }}
        onConfirm={() => void confirmUndo()}
      />
    </div>
  );
}
